import React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { Merchandiser } from '../../../../redux/composableDevice/model/Merchandiser.model';
import { useCurrentStateByControlUnitId } from '../../../../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { getValueWithPreferredUnitAndDecimals } from '../../../../../../redux/controlUnit/api/util/sensor/formatValues';
import usePreferredTemperatureUnit from '../../../../../../redux/settings/hooks/usePreferredTemperatureUnit';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  AirAmbientHumidityIcon,
  AirAmbientTemperatureIcon,
  AirDischargeTemperatureIcon,
} from '../../../../theme/icons';

interface ChannelButtonProps {
  channel: any;
  merchandiser: Merchandiser;
}

const ChannelButton = ({
  channel,
  channelIndex,
}: ChannelButtonProps & { channelIndex: number }) => {
  const theme = useTheme();
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();

  const {
    channel: channelId = '',
    controlUnitId = '',
    deviceId: commanderId = '',
    channelType = '',
  } = channel || {};

  const { currentSensorData, loading } = useCurrentStateByControlUnitId(
    commanderId,
    controlUnitId
  );

  let displayValue = '';
  let displayUnit = '';

  if (channelType === 'sensordata') {
    const sensor = currentSensorData?.[channelId];
    const sensorValue = sensor?.value;
    const sensorUnit =
      sensor?.metadata?.unitString ?? sensor?.metadata?.unit ?? '';

    const { value, unit } = getValueWithPreferredUnitAndDecimals(
      sensorValue,
      sensorUnit,
      0,
      preferredTemperatureUnit
    );

    displayValue = value;
    displayUnit = unit;
  } else if (channelType === 'action' || channelType === 'event') {
    displayValue = channel?.name || '';
    displayUnit = '';
  }

  if (loading && !displayValue) {
    return <LoadingButton />;
  }

  const icons = [
    {
      name: 'Product Simulator',
      icon: <AirDischargeTemperatureIcon height={'1.5rem'} color="primary" />,
    },
    {
      name: 'Ambient air temperature',
      icon: <AirAmbientTemperatureIcon height={'1.5rem'} color="primary" />,
    },
    {
      name: 'Ambient air humidity',
      icon: <AirAmbientHumidityIcon height={'1.5rem'} color="primary" />,
    },
  ];

  return (
    <Button
      variant="outlined"
      disableRipple={true}
      sx={{
        borderRadius: '1rem',
        borderColor: 'transparent',
        backgroundColor: theme.palette.primary.contrastText,
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        padding: '0.7rem 1rem',
        minWidth: '8.5rem',
        maxWidth: '8.5rem',
        height: '3.5rem',

        '&:hover': {
          backgroundColor: theme.palette.primary.contrastText,
          borderColor: 'transparent',
          cursor: 'default',
        },
      }}
    >
      {icons
        .filter((icon) => icon.name === channel.name)
        .map((icon) => icon.icon)}
      <Box
        display={'flex'}
        flexDirection="row"
        alignItems={'start'}
        alignSelf={'baseline'}
      >
        <Typography variant="body2" fontSize="1.5rem" color="text.primary">
          {displayValue}
        </Typography>
        {displayUnit && (
          <Typography variant="caption" fontSize="1.3rem" color="text.primary">
            {displayUnit}
          </Typography>
        )}
      </Box>
    </Button>
  );
};

interface MerchandiserResumeProps {
  merchandiser: Merchandiser;
  onChannelClick?: (channelIndex: number) => void;
}

export const MerchandiserResume = ({
  merchandiser,
}: MerchandiserResumeProps) => {
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.machineType.id,
    })
  );

  const merchandiserStandardViewChannels = JSON.parse(
    merchandiser?.composableDevice?.labels?.['standard_view_channels'] || '[]'
  );

  const composableDeviceStandardViewChannels = JSON.parse(
    machineType?.labels['standard_view_channels'] || '[]'
  );

  const standardViewChannels =
    merchandiserStandardViewChannels.length > 0
      ? merchandiserStandardViewChannels
      : composableDeviceStandardViewChannels || [];

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
        {standardViewChannels.map((channelIndex: number) => {
          const channel = merchandiser?.channels[channelIndex];
          if (!channel) return null;

          return (
            <ChannelButton
              key={`standard-${channelIndex}`}
              channelIndex={channelIndex}
              channel={channel}
              merchandiser={merchandiser}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default MerchandiserResume;
